import React, { useState, useEffect, useCallback } from "react";
import config from "../../Config";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import { NavLink } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AccountsMenu from "./AccountsMenu";
import YearsDropDown from "../Shared/YearsDropDown";
import Pagination from "../General/Pagination";

function parseAmount(value) {
  const n = parseFloat(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function mapRowFromApi(item) {
  return {
    key: `${item.AccountID}-${item.OpBalanceID || 0}`,
    opBalanceID: item.OpBalanceID || 0,
    accountID: item.AccountID,
    account_Code: item.Account_Code || "",
    account_Description: item.Account_Description || "",
    dr_Amt: item.DR_Amt ?? 0,
    cr_Amt: item.CR_Amt ?? 0,
    budget_Allocation: item.Budget_Allocation ?? 0,
    dirty: false,
  };
}

function ManageOpeningBalances() {
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));
  const [FYear, setFYear] = useState(
    () => localStorage.getItem("DefaultFYear") || ""
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [accountCode, setAccountCode] = useState("");
  const [accountName, setAccountName] = useState("");
  const [appliedCode, setAppliedCode] = useState("");
  const [appliedName, setAppliedName] = useState("");

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalDR, setTotalDR] = useState(0);
  const [totalCR, setTotalCR] = useState(0);
  const [totalBudget, setTotalBudget] = useState(0);

  const fetchRows = useCallback(
    (targetPage) => {
      const year = parseInt(FYear, 10);
      if (!year) {
        setRows([]);
        setTotalRecords(0);
        return;
      }

      setLoading(true);
      axios({
        method: "post",
        url:
          config.base_url + "Accounts/GetThirdLevelOpeningBalancesForYear",
        headers: { "Content-Type": "application/json" },
        data: {
          AccessKey,
          UserID,
          MemberType,
          FYear: year,
          pageNo: targetPage ?? pageNo,
          pageSize,
          AccountCode: appliedCode,
          AccountName: appliedName,
        },
      })
        .then((response) => {
          const list = Array.isArray(response.data?.ListofRecords)
            ? response.data.ListofRecords
            : [];
          setRows(list.map(mapRowFromApi));
          setTotalRecords(response.data?.totalRecords ?? 0);
          setTotalDR(response.data?.TotalDR ?? 0);
          setTotalCR(response.data?.TotalCR ?? 0);
          setTotalBudget(response.data?.TotalBudget ?? 0);
        })
        .catch(() => {
          setRows([]);
          toast.error("Network Error..", {
            position: toast.POSITION.TOP_RIGHT,
          });
        })
        .finally(() => setLoading(false));
    },
    [
      AccessKey,
      UserID,
      MemberType,
      FYear,
      pageNo,
      pageSize,
      appliedCode,
      appliedName,
    ]
  );

  useEffect(() => {
    document.title = "Manage Opening Balances";
    fetchRows();
  }, [fetchRows]);

  const handleSearch = () => {
    setAppliedCode(accountCode.trim());
    setAppliedName(accountName.trim());
    if (pageNo === 1) {
      fetchRows(1);
    } else {
      setPageNo(1);
    }
  };

  const updateRow = (key, field, value) => {
    setRows((prev) =>
      prev.map((row) =>
        row.key === key ? { ...row, [field]: value, dirty: true } : row
      )
    );
  };

  const handleSaveAll = () => {
    const year = parseInt(FYear, 10);
    if (!year) {
      toast.error("Please select a financial year.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    const dirtyRows = rows.filter((r) => r.dirty);
    if (dirtyRows.length === 0) {
      toast.info("No changes to save on this page.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    for (const row of dirtyRows) {
      const dr = parseAmount(row.dr_Amt);
      const cr = parseAmount(row.cr_Amt);
      if (dr > 0 && cr > 0) {
        toast.error(
          `${row.account_Code}: enter either debit or credit, not both.`,
          { position: toast.POSITION.TOP_RIGHT }
        );
        return;
      }
    }

    setSaving(true);
    axios({
      method: "post",
      url: config.base_url + "Accounts/SaveOpeningBalancesBulk",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey,
        UserID,
        MemberType,
        FYear: year,
        OpeningBalanceRows: dirtyRows.map((row) => ({
          OpBalanceID: row.opBalanceID,
          AccountID: row.accountID,
          DR_Amt: parseAmount(row.dr_Amt),
          CR_Amt: parseAmount(row.cr_Amt),
          Budget_Allocation: parseAmount(row.budget_Allocation),
        })),
      },
    })
      .then((response) => {
        if (response.data?.status_code === 1) {
          toast.success(response.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
          });
          fetchRows(pageNo);
        } else {
          toast.error(response.data?.status_message || "Could not save.", {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch(() => {
        toast.error("Network Error..", {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .finally(() => setSaving(false));
  };

  const dirtyCount = rows.filter((r) => r.dirty).length;

  return (
    <>
      <AccountsMenu />
      <ToastContainer />
      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-6">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <NavLink to="/Dashboard">Dashboard</NavLink>
                    </li>
                    <li className="breadcrumb-item">
                      <NavLink to="/Accounts/AccountsDashboard">Accounts</NavLink>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Manage Opening Balances
                    </li>
                  </ol>
                </nav>
                <div className="page-title">Manage Opening Balances</div>
                <p className="text-muted small mb-0">
                  All 3rd level accounts for the selected year. Enter debit,
                  credit, and budget allocation, then save.
                </p>
              </div>
              <div className="col-md-6 text-md-end">
                <NavLink
                  to="/Accounts/OpeningBalances"
                  className="button button-white button-icon"
                >
                  View Opening Balances
                </NavLink>
              </div>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="container">
            <div className="card">
              <div className="card-body">
                <div className="row gy-3 align-items-end">
                  <div className="col-md-2">
                    <label>Financial Year</label>
                    <select
                      className="form-select"
                      value={FYear}
                      onChange={(e) => setFYear(e.target.value)}
                    >
                      <option value="">Select year</option>
                      <YearsDropDown selected={FYear} />
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label>Account Code</label>
                    <input
                      type="text"
                      className="form-control"
                      value={accountCode}
                      onChange={(e) => setAccountCode(e.target.value)}
                      placeholder="e.g. 10101"
                    />
                  </div>
                  <div className="col-md-3">
                    <label>Account Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={accountName}
                      onChange={(e) => setAccountName(e.target.value)}
                      placeholder="Search by name"
                    />
                  </div>
                  <div className="col-md-2">
                    <button
                      type="button"
                      className="button button-primary w-100"
                      onClick={handleSearch}
                      disabled={loading}
                    >
                      Search
                    </button>
                  </div>
                  <div className="col-md-2">
                    <button
                      type="button"
                      className="button button-primary w-100"
                      onClick={handleSaveAll}
                      disabled={saving || loading || dirtyCount === 0}
                    >
                      {saving
                        ? "Saving…"
                        : `Save Changes${dirtyCount ? ` (${dirtyCount})` : ""}`}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="card mt-3">
              <div className="card-body table-wrap">
                <table className="table table-theme table-hover">
                  <thead>
                    <tr>
                      <th>Sr#</th>
                      <th>Account Code</th>
                      <th>Account Name</th>
                      <th>Debit</th>
                      <th>Credit</th>
                      <th>Budget Allocation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="text-center">
                          <Spinner animation="border" role="status" />
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center text-muted">
                          No third level accounts found.
                        </td>
                      </tr>
                    ) : (
                      rows.map((row, index) => (
                        <tr
                          key={row.key}
                          className={row.dirty ? "table-warning" : ""}
                        >
                          <td>{index + 1 + pageSize * (pageNo - 1)}</td>
                          <td>{row.account_Code}</td>
                          <td>{row.account_Description}</td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="form-control form-control-sm text-end"
                              value={row.dr_Amt}
                              onChange={(e) =>
                                updateRow(row.key, "dr_Amt", e.target.value)
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="form-control form-control-sm text-end"
                              value={row.cr_Amt}
                              onChange={(e) =>
                                updateRow(row.key, "cr_Amt", e.target.value)
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="form-control form-control-sm text-end"
                              value={row.budget_Allocation}
                              onChange={(e) =>
                                updateRow(
                                  row.key,
                                  "budget_Allocation",
                                  e.target.value
                                )
                              }
                            />
                          </td>
                        </tr>
                      ))
                    )}
                    {!loading && rows.length > 0 && (
                      <tr className="fw-bold">
                        <td colSpan="3" className="text-end">
                          Filtered totals:
                        </td>
                        <td className="text-end">{totalDR}</td>
                        <td className="text-end">{totalCR}</td>
                        <td className="text-end">{totalBudget}</td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {totalRecords > pageSize && (
                  <div className="pagination-wrap mt-3">
                    <div className="row align-items-center">
                      <div className="col-md-2">
                        <select
                          className="form-select"
                          style={{ width: "100px" }}
                          value={pageSize}
                          onChange={(e) => {
                            setPageNo(1);
                            setPageSize(parseInt(e.target.value, 10));
                          }}
                        >
                          <option value="50">50</option>
                          <option value="100">100</option>
                          <option value="200">200</option>
                          <option value="500">500</option>
                        </select>
                      </div>
                      <Pagination
                        pageNo={pageNo}
                        pageSize={pageSize}
                        totalRecords={totalRecords}
                        onPageChange={setPageNo}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ManageOpeningBalances;
